import { queryClient } from "src/queryClient";

/**
 * Want to introduce a new query key? Just update me!
 *
 * @private
 */
type Config = {
    message: { forward: string };
    messages: [
        { devtools: ["inbox", { id: string }] },
        { inbox: [string] },
        "sent",
        "draft",
        "scheduled",
        "render-sms",
        {
            poll: {
                content: [string];
                insights: { count: [string]; option: Record<string, string> };
            };
            detail: [string];
            duration: [string];
            sentDetails: [string];
            notes: [string];
            insights: { detail: [string]; metrics: Record<string, string>; chart: Record<string, string> };
            composer: { message: [string]; groups: { search: [string]; table: [string] } };
            id: [string];
        },
    ];
    ipaws: ["drafts", "alerts"];
    topicTree: ["list"];
    templates: ["list", { composeDetail: [string]; detail: [string]; edit: [string] }, { senderOwner: [string] }];
    document: [string, { presignedUrl: [string] }];
    map: { customer: ["bounds", "geofilters", "userAddresses", { geofiltersWithin: [string] }]; geofilters: string };
    compose: ["senders", "projected"];
    sandbox: ["drafts"];
    user: [
        "list",
        "permissions",
        { id: [string] },
        {
            search: {
                nameQuery: Record<
                    string,
                    { groupId: Record<string, { roleId: Record<string, { groupless: Record<string, { status: [string] }> }> }> }
                >;
            };
        },
    ];
    roles: ["list"];
    group: [
        "rootGroup",
        {
            id: {
                [_ in string]: [
                    "allMembers",
                    "base",
                    "children",
                    "details",
                    "childrenPaged",
                    "directMembers",
                    "directMembersCount",
                    "parentsPaged",
                    "rowPin",
                    "senders",
                    "blockers",
                ];
            };
        },
        { search: [{ table: [string] }, { filter: [string] }] },
    ];
    customer: ["features", "languages", "whiteLabeling", "id-subdomain", { "blackout-period-contains": string }];
    topic: ["rootTopic", { id: { [_ in string]: ["members", "children", "details", "blockers"] } }, "theWholeTree"];
    settings: ["rootGroup", "rootTopic"];
    senders: ["list", { id: { [_ in string]: ["blockers"] } }, "phoneNumbers", "emails"];
    support: [
        "user-manage",
        { "user-manage": string; user: string },
        "ingest-rules",
        { "ingest-rules": "templates" },
        "phone-numbers",
        { "do-not-send-list": { search: string; id: string } },
    ];
};

// *** //

/** @private */
type Resolved<T, Ctx extends ReadonlyArray<string> = []> = T extends readonly (infer Each)[]
    ? Each extends Each
        ? Resolved<Each, Ctx>
        : never
    : T extends string
      ? [...Ctx, T]
      : T extends Record<infer Key extends string, unknown>
        ? Key extends Key
            ? Resolved<T[Key], [...Ctx, Key]>
            : never
        : never;

/** @private */
type Fuzzy<T, Ctx extends ReadonlyArray<string> = []> = T extends readonly [infer Head extends string, ...infer Rest]
    ? [...Ctx, Head] | Fuzzy<Rest, [...Ctx, Head]>
    : never;

/**
 * A predetermined subtype of {@link QueryKey} for use throughout EMNS. Standardized query keys allow for cache reuse with confience that there won't be typos or unexpected keys supplied.
 */
export type EmnsQK = string[]; // eslint-disable-line @typescript-eslint/naming-convention

export const qk = Object.assign(
    /**
     * Constructs a type-safe {@link QueryKey} via {@link EmnsQK}.
     */
    (...args: EmnsQK) => args,
    {
        /** Invalidates the given partial {@link EmnsQK} */
        invalidate: (...queryKey: Fuzzy<Resolved<Config>>) => queryClient.invalidateQueries({ queryKey, exact: false, refetchType: "all" }),
    },
);
